import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  MapPin, Clock, Star, ChevronRight, Utensils, 
  Wind, Umbrella, Waves, ShoppingBasket, Pill, Snowflake,
  Flame, Tag
} from 'lucide-react';
import { DeliveryZoneMap } from '../components/DeliveryZoneMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const { t, language } = useLanguage();
  const [restaurants, setRestaurants] = useState([]);
  const [featuredRestaurants, setFeaturedRestaurants] = useState([]);
  const [specialMenus, setSpecialMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platformStatus, setPlatformStatus] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [restRes, catRes, featuredRes, menusRes, cfgRes] = await Promise.all([
          fetch(`${API_URL}/api/restaurants`),
          fetch(`${API_URL}/api/service-categories`),
          fetch(`${API_URL}/api/restaurants/featured/list`),
          fetch(`${API_URL}/api/special-menus`),
          fetch(`${API_URL}/api/config/public`)
        ]);
        
        if (restRes.ok) {
          const restData = await restRes.json();
          setRestaurants(restData.sort((a, b) => ((b.is_open_now !== false) - (a.is_open_now !== false))).slice(0, 4));
        }
        if (catRes.ok) setCategories(await catRes.json());
        if (featuredRes.ok) setFeaturedRestaurants(await featuredRes.json());
        if (menusRes.ok) setSpecialMenus(await menusRes.json());
        if (cfgRes.ok) setPlatformStatus(await cfgRes.json());
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getIcon = (iconName) => {
    const icons = {
      'utensils': Utensils,
      'wind': Wind,
      'umbrella': Umbrella,
      'snowflake': Snowflake,
      'shopping-basket': ShoppingBasket,
      'pills': Pill
    };
    const Icon = icons[iconName] || Utensils;
    return <Icon size={24} />;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Platform closed banner */}
      {platformStatus && platformStatus.platform_is_open === false && (
        <div className="bg-[#E53935] text-white px-4 py-3" data-testid="platform-closed-banner">
          <div className="container mx-auto flex items-center gap-3">
            <Clock size={20} className="flex-shrink-0" />
            <p className="text-sm md:text-base font-semibold">
              {language === 'pt'
                ? (platformStatus.closed_message || 'De momento não estamos a aceitar pedidos. Voltamos em breve!')
                : (platformStatus.closed_message_en || platformStatus.closed_message || "We are not accepting orders at the moment. We'll be back soon!")}
            </p>
          </div>
        </div>
      )}
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1726046330780-282a189c6d6a?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#004E64]/60 to-[#004E64]/80" />
        
        <div className="relative container mx-auto px-4 py-20 md:py-32">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6" data-testid="hero-title">
              {t('heroTitle')}
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8">
              {t('heroSubtitle')}
            </p>
            
            {/* Location Badge */}
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full mb-8">
              <MapPin className="text-[#2EC4B6]" size={20} />
              <span className="text-white font-medium">{t('deliveryAvailable')}</span>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Link to="/restaurants">
                <Button 
                  className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                  data-testid="view-restaurants-btn"
                >
                  <Utensils className="mr-2" size={20} />
                  {t('viewRestaurants')}
                </Button>
              </Link>
              <Link to="/shisha">
                <Button 
                  variant="outline"
                  className="bg-white/10 backdrop-blur-md border-white/30 text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg font-bold transition-all"
                  data-testid="rent-shisha-btn"
                >
                  <Wind className="mr-2" size={20} />
                  {t('rentShisha')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Wave decoration */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#FDFBF7"/>
          </svg>
        </div>
      </section>

      {/* Featured Restaurants — FIRST */}
      {featuredRestaurants.length > 0 && (
        <section className="container mx-auto px-4 py-12" data-testid="featured-restaurants-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#FF6B35]/10 flex items-center justify-center">
              <Flame className="text-[#FF6B35]" size={20} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#004E64]">
              {language === 'pt' ? 'Restaurantes em Destaque' : 'Featured Restaurants'}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredRestaurants.map(restaurant => (
              <Link key={restaurant.restaurant_id} to={`/restaurants/${restaurant.restaurant_id}`}>
                <Card className="overflow-hidden rounded-2xl border-2 border-[#FF6B35]/20 hover:border-[#FF6B35]/50 shadow-sm hover:shadow-lg transition-all group cursor-pointer" data-testid={`featured-${restaurant.restaurant_id}`}>
                  <div className="relative h-40 overflow-hidden">
                    <img src={restaurant.cover_image || restaurant.logo} alt={restaurant.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <Badge className="absolute top-3 left-3 bg-[#FF6B35] text-white text-xs px-2 py-1">
                      <Flame size={12} className="mr-1" />
                      {language === 'pt' ? 'Destaque' : 'Featured'}
                    </Badge>
                    <Badge className={`absolute top-10 left-3 text-white text-xs px-2 py-1 ${restaurant.is_active === false || restaurant.is_out_of_stock ? 'bg-red-500 hover:bg-red-500' : restaurant.is_open_now !== false ? 'bg-green-600 hover:bg-green-600' : 'bg-gray-500 hover:bg-gray-500'}`} data-testid={`open-badge-${restaurant.restaurant_id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${restaurant.is_open_now !== false ? 'bg-white animate-pulse' : 'bg-white/60'}`} />
                      {restaurant.is_active === false || restaurant.is_out_of_stock
                        ? (language === 'pt' ? 'Indisponível' : 'Unavailable')
                        : restaurant.is_open_now !== false
                          ? (language === 'pt' ? 'Aberto' : 'Open')
                          : (language === 'pt' ? 'Fechado' : 'Closed')}
                    </Badge>
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                      <Star className="text-yellow-500 fill-yellow-500" size={12} />
                      <span className="text-xs font-bold text-[#004E64]">{restaurant.rating}</span>
                    </div>
                    <div className="absolute bottom-3 left-3">
                      <h3 className="font-bold text-white text-lg">{restaurant.name}</h3>
                      <p className="text-white/80 text-xs">{restaurant.cuisine_type}</p>
                    </div>
                  </div>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-[#9FA2B4]">
                      <span className="flex items-center gap-1"><Clock size={12} />{restaurant.delivery_time}</span>
                      <span className="text-[#FF6B35] font-medium">{t('deliveryFrom')}</span>
                    </div>
                    {restaurant.min_order > 0 && (
                      <span className="text-xs text-[#9FA2B4]">Min. €{restaurant.min_order?.toFixed(2)}</span>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Special Menus — SECOND */}
      {specialMenus.length > 0 && (
        <section className="container mx-auto px-4 py-12" data-testid="special-menus-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#2EC4B6]/10 flex items-center justify-center">
              <Tag className="text-[#2EC4B6]" size={20} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#004E64]">
              {language === 'pt' ? 'Menus Especiais' : 'Special Menus'}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {specialMenus.map(menu => (
              <Link key={menu.menu_id} to={`/special-menu/${menu.menu_id}`} className="block" data-testid={`special-menu-link-${menu.menu_id}`}>
              <Card className="overflow-hidden rounded-2xl border border-[#2EC4B6]/20 hover:border-[#2EC4B6]/50 shadow-sm hover:shadow-lg transition-all cursor-pointer h-full" data-testid={`special-menu-${menu.menu_id}`}>
                {menu.image && (
                  <div className="relative h-36 overflow-hidden">
                    <img src={menu.image.startsWith('http') ? menu.image : `${API_URL}${menu.image}`} alt={menu.name} className="w-full h-full object-cover" />
                    {menu.original_price && menu.original_price > menu.price && (
                      <Badge className="absolute top-3 right-3 bg-red-500 text-white text-xs">
                        -{Math.round((1 - menu.price / menu.original_price) * 100)}%
                      </Badge>
                    )}
                    {menu.source_type === 'shisha' && (
                      <Badge className="absolute top-3 left-3 bg-purple-500 text-white text-xs">
                        <Wind size={12} className="mr-1" /> Shisha
                      </Badge>
                    )}
                    {menu.source_type === 'ice' && (
                      <Badge className="absolute top-3 left-3 bg-blue-500 text-white text-xs">
                        <Snowflake size={12} className="mr-1" /> {language === 'pt' ? 'Gelo' : 'Ice'}
                      </Badge>
                    )}
                  </div>
                )}
                {!menu.image && menu.source_type === 'shisha' && (
                  <div className="relative h-36 overflow-hidden bg-gradient-to-br from-purple-100 to-purple-50 flex items-center justify-center">
                    <Wind size={48} className="text-purple-300" />
                    <Badge className="absolute top-3 left-3 bg-purple-500 text-white text-xs">
                      <Wind size={12} className="mr-1" /> Shisha
                    </Badge>
                    {menu.original_price && menu.original_price > menu.price && (
                      <Badge className="absolute top-3 right-3 bg-red-500 text-white text-xs">
                        -{Math.round((1 - menu.price / menu.original_price) * 100)}%
                      </Badge>
                    )}
                  </div>
                )}
                {!menu.image && menu.source_type === 'ice' && (
                  <div className="relative h-36 overflow-hidden bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                    <Snowflake size={48} className="text-blue-300" />
                    <Badge className="absolute top-3 left-3 bg-blue-500 text-white text-xs">
                      <Snowflake size={12} className="mr-1" /> {language === 'pt' ? 'Gelo' : 'Ice'}
                    </Badge>
                    {menu.original_price && menu.original_price > menu.price && (
                      <Badge className="absolute top-3 right-3 bg-red-500 text-white text-xs">
                        -{Math.round((1 - menu.price / menu.original_price) * 100)}%
                      </Badge>
                    )}
                  </div>
                )}
                <CardContent className="p-4">
                  <h3 className="font-bold text-[#004E64] text-lg mb-1">
                    {language === 'pt' ? (menu.name_pt || menu.name) : menu.name}
                  </h3>
                  <p className="text-[#9FA2B4] text-sm mb-3 line-clamp-2">
                    {language === 'pt' ? (menu.description_pt || menu.description) : menu.description}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-[#FF6B35]">€{menu.price?.toFixed(2)}</span>
                    {menu.original_price && menu.original_price > menu.price && (
                      <span className="text-sm text-[#9FA2B4] line-through">€{menu.original_price?.toFixed(2)}</span>
                    )}
                  </div>
                  {menu.available_hours_start && menu.available_hours_end && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-[#2EC4B6]">
                      <Clock size={12} />
                      {menu.available_hours_start} - {menu.available_hours_end}
                    </div>
                  )}
                </CardContent>
              </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Services Section — THIRD */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-[#004E64] mb-8">{t('exploreServices')}</h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            Array(3).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-48 rounded-3xl" />
            ))
          ) : (
            categories.filter(c => c.is_active || c.is_coming_soon).map(category => {
              const isComingSoon = category.is_available === false || (category.is_coming_soon === true && category.is_available === undefined);
              // Determine the correct route based on service type
              let serviceRoute = category.route;
              // If route is generic or undefined, use the dynamic service page
              if (!serviceRoute || serviceRoute === '/' || serviceRoute === '/restaurants') {
                // Only use /restaurants for Food Delivery category
                if (category.name === 'Food Delivery' || category.name_pt === 'Entrega de Comida') {
                  serviceRoute = '/restaurants';
                } else {
                  serviceRoute = `/service/${category.category_id}`;
                }
              }
              
              return (
                <Link 
                  key={category.category_id} 
                  to={isComingSoon ? '#' : serviceRoute}
                  className={isComingSoon ? 'cursor-not-allowed' : ''}
                >
                  <Card 
                    className={`service-card h-48 overflow-hidden ${isComingSoon ? 'opacity-60' : ''}`}
                    data-testid={`service-${category.category_id}`}
                  >
                    <img 
                      src={category.image} 
                      alt={language === 'pt' ? category.name_pt : category.name}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <CardContent className="relative z-20 h-full flex flex-col justify-end p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white">
                          {getIcon(category.icon)}
                        </div>
                        {isComingSoon && (
                          <span className="badge-beach">{t('comingSoon')}</span>
                        )}
                      </div>
                      <h3 className="text-xl font-bold text-white">
                        {language === 'pt' ? category.name_pt : category.name}
                      </h3>
                      <p className="text-white/80 text-sm">
                        {language === 'pt' ? category.description_pt : category.description}
                      </p>
                    </CardContent>
                </Card>
              </Link>
              );
            })
          )}
        </div>
      </section>

      {/* All Restaurants */}
      <section className="container mx-auto px-4 py-16 bg-[#F4F1EA] rounded-3xl mx-4">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004E64]">
            {language === 'pt' ? 'Todos os Restaurantes' : 'All Restaurants'}
          </h2>
          <Link to="/restaurants">
            <Button variant="ghost" className="text-[#FF6B35] font-semibold">
              {t('all')} <ChevronRight size={20} />
            </Button>
          </Link>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {loading ? (
            Array(4).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-64 rounded-3xl" />
            ))
          ) : (
            restaurants.map(restaurant => (
              <Link key={restaurant.restaurant_id} to={`/restaurants/${restaurant.restaurant_id}`}>
                <Card 
                  className="restaurant-card bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-[#004E64]/5 group cursor-pointer"
                  data-testid={`restaurant-${restaurant.restaurant_id}`}
                >
                  <div className="relative h-36 overflow-hidden">
                    <img 
                      src={restaurant.cover_image || restaurant.logo} 
                      alt={restaurant.name}
                      className={`restaurant-image w-full h-full object-cover ${restaurant.is_open_now === false ? 'grayscale' : ''}`}
                    />
                    <Badge className={`absolute top-3 left-3 text-white text-xs ${restaurant.is_active === false || restaurant.is_out_of_stock ? 'bg-red-500 hover:bg-red-500' : restaurant.is_open_now !== false ? 'bg-green-600 hover:bg-green-600' : 'bg-gray-500 hover:bg-gray-500'}`} data-testid={`home-open-badge-${restaurant.restaurant_id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${restaurant.is_open_now !== false ? 'bg-white animate-pulse' : 'bg-white/60'}`} />
                      {restaurant.is_active === false || restaurant.is_out_of_stock
                        ? (language === 'pt' ? 'Indisponível' : 'Unavailable')
                        : restaurant.is_open_now !== false
                          ? (language === 'pt' ? 'Aberto' : 'Open')
                          : (language === 'pt' ? 'Fechado' : 'Closed')}
                    </Badge>
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                      <Star className="text-yellow-500 fill-yellow-500" size={14} />
                      <span className="text-sm font-bold text-[#004E64]">{restaurant.rating}</span>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-[#004E64] text-lg mb-1">{restaurant.name}</h3>
                    <p className="text-[#9FA2B4] text-sm mb-3 line-clamp-1">{restaurant.cuisine_type}</p>
                    <div className="flex items-center gap-4 text-sm text-[#9FA2B4]">
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {restaurant.delivery_time}
                      </span>
                      <span className="text-[#FF6B35] font-medium">{t('deliveryFrom')}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* Delivery Zone Map */}
      <section className="container mx-auto px-4 py-16" data-testid="delivery-zone-section">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <MapPin className="text-[#FF6B35]" size={28} />
              <h2 className="text-3xl md:text-4xl font-bold text-[#004E64]">
                {language === 'pt' ? 'Entregamos aqui' : 'We deliver here'}
              </h2>
            </div>
            <p className="text-[#5C6B73] mb-6">
              {language === 'pt'
                ? 'Toda a península de Tróia está coberta — de Tróia Mar a Sol Tróia. Se estás dentro da zona no mapa, entregamos onde estiveres.'
                : 'The entire Tróia peninsula is covered — from Tróia Mar to Sol Tróia. If you are inside the zone on the map, we deliver wherever you are.'}
            </p>
            <ul className="space-y-3">
              {[
                language === 'pt' ? 'Hotéis e resorts (Tróia Resort, Sol Tróia...)' : 'Hotels and resorts (Tróia Resort, Sol Tróia...)',
                language === 'pt' ? 'Casas e apartamentos' : 'Homes and apartments',
                language === 'pt' ? 'Direto na areia — Beach Mode' : 'Right on the sand — Beach Mode',
              ].map((txt, i) => (
                <li key={i} className="flex items-center gap-3 text-[#004E64]">
                  <span className="w-8 h-8 rounded-full bg-[#2EC4B6]/15 flex items-center justify-center flex-shrink-0">
                    <MapPin size={15} className="text-[#2EC4B6]" />
                  </span>
                  <span className="font-medium">{txt}</span>
                </li>
              ))}
            </ul>
          </div>
          <DeliveryZoneMap />
        </div>
      </section>

      {/* Beach Mode Banner */}
      <section className="container mx-auto px-4 py-16">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#2EC4B6] to-[#1DA1A1] p-8 md:p-12">
          <div className="relative z-10 max-w-xl">
            <div className="flex items-center gap-3 mb-4">
              <Waves className="text-white" size={32} />
              <span className="font-handwritten text-2xl text-white/90">Beach Mode</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {language === 'pt' ? 'Entrega direto na praia!' : 'Delivery right to the beach!'}
            </h2>
            <p className="text-white/90 mb-6">
              {language === 'pt' 
                ? 'Receba sua comida, bebidas e shisha diretamente na areia. Perfeito para um dia de praia perfeito.'
                : 'Get your food, drinks and shisha delivered right to the sand. Perfect for a perfect beach day.'}
            </p>
            <Link to="/restaurants">
              <Button className="bg-white text-[#2EC4B6] hover:bg-white/90 rounded-full px-8 py-6 text-lg font-bold">
                {language === 'pt' ? 'Pedir Agora' : 'Order Now'}
              </Button>
            </Link>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute right-0 bottom-0 opacity-20">
            <Umbrella size={200} />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#004E64] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#FF6B35] flex items-center justify-center">
                <span className="text-white font-bold text-xl">W</span>
              </div>
              <div>
                <h3 className="font-bold text-xl">WEGOO</h3>
                <p className="text-white/70 text-sm">Troia Delivery</p>
              </div>
            </div>
            
            <div className="flex gap-8">
              <Link to="/restaurants" className="hover:text-[#FF6B35] transition-colors">{t('restaurants')}</Link>
              <Link to="/shisha" className="hover:text-[#FF6B35] transition-colors">{t('shisha')}</Link>
              <Link to="/orders" className="hover:text-[#FF6B35] transition-colors">{t('orders')}</Link>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-white/70 text-sm">Powered by Adega de Petiscos</p>
              <p className="text-white/50 text-xs mt-1">
                © 2024 WEGOO - Troia Delivery
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
